import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const DIFFICULTY_CONFIG = {
  easy: { label: "Easy", color: "var(--color-success)", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.25)" },
  medium: { label: "Medium", color: "var(--color-warning)", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.25)" },
  hard: { label: "Hard", color: "var(--color-error)", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.25)" },
  extreme: { label: "Extreme", color: "#a855f7", bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.25)" },
};

const CrimeCard = ({ crime, onExecute, territoryBonus = 0, masteryLevel = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const diff = DIFFICULTY_CONFIG?.[crime?.difficulty] || DIFFICULTY_CONFIG?.easy;
  const isOnCooldown = crime?.cooldownRemaining > 0;
  const masteryRiskReduction = Math.floor(masteryLevel / 10);
  const adjustedProfit = Math.round(crime?.maxProfit * (1 + territoryBonus / 100));
  const adjustedSuccess = Math.min(99, Math.round(crime?.baseSuccessRate + (crime?.skillBonus || 0) + (territoryBonus > 0 ? 3 : 0) + masteryRiskReduction));

  const formatCooldown = (secs) => {
    if (secs <= 0) return null;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatReward = (val) => {
    if (val >= 1000000) return `$${(val / 1000000)?.toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000)?.toFixed(0)}K`;
    return `$${val}`;
  };

  return (
    <div
      className="relative rounded-xl border transition-all duration-250 overflow-hidden h-full"
      style={{
        background: isOnCooldown ? "rgba(22,33,62,0.5)" : "var(--color-card)",
        borderColor: isOnCooldown ? "rgba(0,217,255,0.08)" : diff?.border,
        opacity: isOnCooldown ? 0.7 : 1,
      }}
    >
      {/* Difficulty stripe */}
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: diff?.color, boxShadow: `0 0 8px ${diff?.color}` }} />

      <div className="p-4 flex flex-col gap-3">
        {/* Icon + Name (vertical layout) */}
        <div className="flex flex-col items-center text-center gap-2 pt-1">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: diff?.bg, border: `1px solid ${diff?.border}` }}>
            <Icon name={crime?.icon} size={24} color={diff?.color} />
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold text-foreground">{crime?.name}</h3>
            <div className="flex items-center justify-center gap-1.5 mt-0.5">
              <span className="font-caption text-xs px-1.5 py-0.5 rounded" style={{ background: diff?.bg, color: diff?.color }}>{diff?.label}</span>
              {masteryLevel > 0 && (
                <span className="font-caption text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                  Mastery {masteryLevel}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Reward + Risk row */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg p-2 text-center" style={{ background: "rgba(0,217,255,0.06)", border: "1px solid rgba(0,217,255,0.1)" }}>
            <div className="font-caption text-xs text-muted-foreground mb-0.5">Max Reward</div>
            <div className="font-heading text-sm font-bold text-primary">{formatReward(adjustedProfit)}</div>
          </div>
          <div className="rounded-lg p-2 text-center" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.15)" }}>
            <div className="font-caption text-xs text-muted-foreground mb-0.5">Success</div>
            <div className="font-heading text-sm font-bold" style={{ color: "var(--color-success)" }}>{adjustedSuccess}%</div>
          </div>
        </div>

        {/* Energy & Jail */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1">
            <Icon name="Zap" size={11} color="var(--color-accent)" />
            <span className="text-muted-foreground">{crime?.energyCost} Energy</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Clock" size={11} color="var(--color-error)" />
            <span className="text-muted-foreground">{crime?.jailTime}m jail</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Star" size={11} color="var(--color-primary)" />
            <span className="text-muted-foreground">+{crime?.xpReward} XP</span>
          </div>
        </div>

        {/* Expand toggle */}
        <button onClick={() => setExpanded(!expanded)}
          className="flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
          <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={12} color="currentColor" />
          {expanded ? 'Less' : 'Details'}
        </button>

        {/* Expanded details */}
        {expanded && (
          <div className="p-3 rounded-lg space-y-2" style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(0,217,255,0.08)" }}>
            <p className="text-xs text-muted-foreground leading-relaxed">{crime?.description}</p>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <div>
                <div className="font-caption text-xs text-muted-foreground">Min Reward</div>
                <div className="font-caption text-xs text-foreground">{formatReward(crime?.minProfit)}</div>
              </div>
              <div>
                <div className="font-caption text-xs text-muted-foreground">Req. Level</div>
                <div className="font-caption text-xs text-foreground">Lvl {crime?.requiredLevel}</div>
              </div>
              {masteryRiskReduction > 0 && (
                <div className="col-span-2">
                  <div className="font-caption text-xs text-muted-foreground">Mastery Bonus</div>
                  <div className="font-caption text-xs" style={{ color: "var(--color-success)" }}>+{masteryRiskReduction}% success rate</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cooldown or Execute */}
        {isOnCooldown ? (
          <div className="flex items-center justify-center gap-2 py-2 rounded-lg" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(0,217,255,0.1)" }}>
            <Icon name="Clock" size={13} color="var(--color-muted-foreground)" />
            <span className="font-caption text-xs text-muted-foreground">Available in {formatCooldown(crime?.cooldownRemaining)}</span>
          </div>
        ) : (
          <Button variant="default" size="sm" fullWidth iconName={crime?.icon} iconPosition="left" onClick={() => onExecute(crime)}>
            Execute
          </Button>
        )}
      </div>
    </div>
  );
};

export default CrimeCard;